import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../lib/auth/AuthProvider';

interface IdPConfig {
  id: string;
  name: string;
  type: 'oidc';
  issuer: string;
  clientId: string;
  scopes: string[];
  attributeMapping: {
    email: string;
    name?: string;
    picture?: string;
  };
  isActive: boolean;
  scimEnabled: boolean;
  createdAt: string;
  updatedAt: string;
}

interface IdPTestResult {
  success: boolean;
  message?: string;
  error?: string;
  discoveryInfo?: {
    issuer: string;
    authorizationEndpoint: string;
    tokenEndpoint: string;
    userinfoEndpoint: string;
    jwksUri: string;
    supportedScopes: string[];
  };
}

interface IdPConfigFormProps {
  orgId: string;
}

export function IdPConfigForm({ orgId }: IdPConfigFormProps) {
  const { config, getAccessToken } = useAuth();
  const [idps, setIdPs] = useState<IdPConfig[]>([]);
  const [selectedIdP, setSelectedIdP] = useState<IdPConfig | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Form state
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    issuer: '',
    clientId: '',
    clientSecret: '',
    scopes: 'openid email profile',
    emailAttr: 'email',
    nameAttr: 'name',
    pictureAttr: 'picture',
  });
  const [isSaving, setIsSaving] = useState(false);
  const [testResult, setTestResult] = useState<IdPTestResult | null>(null);
  const [isTesting, setIsTesting] = useState(false);

  // API helper
  const apiCall = useCallback(async (path: string, options?: RequestInit) => {
    const token = await getAccessToken();
    if (!token) throw new Error('Not authenticated');

    const response = await fetch(`${config?.api?.endpoint}${path}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        ...options?.headers,
      },
    });

    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.error?.message || 'API request failed');
    }
    return data.data;
  }, [config, getAccessToken]);

  // Load IdPs
  const loadIdPs = useCallback(async () => {
    try {
      setIsLoading(true);
      const data = await apiCall(`/organizations/${orgId}/idps`);
      setIdPs(data);
      if (data.length > 0 && !selectedIdP) {
        setSelectedIdP(data[0]);
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, [apiCall, orgId, selectedIdP]);

  useEffect(() => {
    if (config?.api?.endpoint && orgId) {
      loadIdPs();
    }
  }, [config, orgId]);

  // Reset form
  const resetForm = () => {
    setFormData({
      name: '',
      issuer: '',
      clientId: '',
      clientSecret: '',
      scopes: 'openid email profile',
      emailAttr: 'email',
      nameAttr: 'name',
      pictureAttr: 'picture',
    });
    setTestResult(null);
    setSelectedIdP(null);
  };

  // Populate form with selected IdP
  const editIdP = (idp: IdPConfig) => {
    setSelectedIdP(idp);
    setFormData({
      name: idp.name,
      issuer: idp.issuer,
      clientId: idp.clientId,
      clientSecret: '', // Don't populate secret
      scopes: idp.scopes.join(' '),
      emailAttr: idp.attributeMapping.email,
      nameAttr: idp.attributeMapping.name || 'name',
      pictureAttr: idp.attributeMapping.picture || 'picture',
    });
    setShowForm(true);
    setTestResult(null);
  };

  // Create or update IdP
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setIsSaving(true);

    try {
      const payload = {
        name: formData.name,
        issuer: formData.issuer,
        clientId: formData.clientId,
        ...(formData.clientSecret && { clientSecret: formData.clientSecret }),
        scopes: formData.scopes.split(/\s+/).filter(Boolean),
        attributeMapping: {
          email: formData.emailAttr,
          name: formData.nameAttr || undefined,
          picture: formData.pictureAttr || undefined,
        },
      };

      if (selectedIdP) {
        // Update
        const updated = await apiCall(`/organizations/${orgId}/idps/${selectedIdP.id}`, {
          method: 'PUT',
          body: JSON.stringify(payload),
        });
        setIdPs(idps.map(i => i.id === selectedIdP.id ? updated : i));
        setSuccess('Identity provider updated successfully');
      } else {
        // Create
        if (!formData.clientSecret) {
          setError('Client secret is required for new identity providers');
          return;
        }
        const created = await apiCall(`/organizations/${orgId}/idps`, {
          method: 'POST',
          body: JSON.stringify(payload),
        });
        setIdPs([...idps, created]);
        setSuccess('Identity provider created successfully');
      }

      setShowForm(false);
      resetForm();
      loadIdPs();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsSaving(false);
    }
  };

  // Test IdP connection
  const handleTest = async () => {
    if (!selectedIdP) return;
    setIsTesting(true);
    setTestResult(null);

    try {
      const result = await apiCall(`/organizations/${orgId}/idps/${selectedIdP.id}/test`, {
        method: 'POST',
      });
      setTestResult(result);
    } catch (err: any) {
      setTestResult({ success: false, error: err.message });
    } finally {
      setIsTesting(false);
    }
  };

  // Delete IdP
  const handleDelete = async (idpId: string) => {
    if (!confirm('Are you sure you want to delete this identity provider?')) return;

    try {
      await apiCall(`/organizations/${orgId}/idps/${idpId}`, {
        method: 'DELETE',
      });
      setIdPs(idps.filter(i => i.id !== idpId));
      if (selectedIdP?.id === idpId) {
        resetForm();
        setShowForm(false);
      }
      setSuccess('Identity provider deleted');
    } catch (err: any) {
      setError(err.message);
    }
  };

  // Toggle active status
  const toggleActive = async (idp: IdPConfig) => {
    try {
      const updated = await apiCall(`/organizations/${orgId}/idps/${idp.id}`, {
        method: 'PUT',
        body: JSON.stringify({ isActive: !idp.isActive }),
      });
      setIdPs(idps.map(i => i.id === idp.id ? updated : i));
    } catch (err: any) {
      setError(err.message);
    }
  };

  if (isLoading) {
    return (
      <div className="p-6 text-center text-muted-foreground">
        Loading identity providers...
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl" id="idp">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-foreground">Identity Provider Settings</h2>
        <p className="text-muted-foreground">
          Configure OIDC identity providers for SSO login.
        </p>
      </div>

      {/* Messages */}
      {error && (
        <div className="mb-4 p-3 bg-destructive/10 border border-destructive/20 rounded-md">
          <p className="text-sm text-destructive">{error}</p>
        </div>
      )}
      {success && (
        <div className="mb-4 p-3 bg-green-500/10 border border-green-500/20 rounded-md">
          <p className="text-sm text-green-600">{success}</p>
        </div>
      )}

      {/* IdP List */}
      {!showForm && (
        <div className="space-y-4">
          {idps.length === 0 ? (
            <div className="p-4 bg-muted rounded-lg text-center">
              <p className="text-muted-foreground mb-4">
                No identity providers configured. Add one to enable SSO.
              </p>
              <button
                onClick={() => {
                  resetForm();
                  setShowForm(true);
                }}
                className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90"
              >
                Add Identity Provider
              </button>
            </div>
          ) : (
            <>
              <div className="flex justify-end">
                <button
                  onClick={() => {
                    resetForm();
                    setShowForm(true);
                  }}
                  className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90"
                >
                  Add Identity Provider
                </button>
              </div>

              {idps.map((idp) => (
                <div key={idp.id} className="p-4 bg-muted rounded-lg">
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="font-semibold flex items-center gap-2">
                        {idp.name}
                        {idp.isActive ? (
                          <span className="px-2 py-0.5 text-xs bg-green-500/20 text-green-600 rounded-full">
                            Active
                          </span>
                        ) : (
                          <span className="px-2 py-0.5 text-xs bg-gray-500/20 text-gray-500 rounded-full">
                            Inactive
                          </span>
                        )}
                      </h3>
                      <p className="text-sm text-muted-foreground mt-1">
                        Issuer: {idp.issuer}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        Client ID: {idp.clientId}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => toggleActive(idp)}
                        className="px-3 py-1 text-sm bg-secondary text-secondary-foreground rounded hover:bg-secondary/80"
                      >
                        {idp.isActive ? 'Deactivate' : 'Activate'}
                      </button>
                      <button
                        onClick={() => editIdP(idp)}
                        className="px-3 py-1 text-sm bg-secondary text-secondary-foreground rounded hover:bg-secondary/80"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(idp.id)}
                        className="px-3 py-1 text-sm bg-destructive/10 text-destructive rounded hover:bg-destructive/20"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </>
          )}
        </div>
      )}

      {/* IdP Form */}
      {showForm && (
        <div className="p-4 bg-muted rounded-lg">
          <h3 className="text-lg font-semibold mb-4">
            {selectedIdP ? 'Edit Identity Provider' : 'Add Identity Provider'}
          </h3>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <label className="block text-sm font-medium mb-1">Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Okta, Azure AD, etc."
                  className="w-full px-3 py-2 bg-background border border-input rounded-md"
                  required
                />
              </div>

              <div className="col-span-2">
                <label className="block text-sm font-medium mb-1">Issuer URL</label>
                <input
                  type="url"
                  value={formData.issuer}
                  onChange={(e) => setFormData({ ...formData, issuer: e.target.value })}
                  placeholder="https://your-tenant.okta.com"
                  className="w-full px-3 py-2 bg-background border border-input rounded-md"
                  required
                />
                <p className="text-xs text-muted-foreground mt-1">
                  The OIDC issuer URL from your identity provider
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Client ID</label>
                <input
                  type="text"
                  value={formData.clientId}
                  onChange={(e) => setFormData({ ...formData, clientId: e.target.value })}
                  placeholder="0oa1234567890abcdef"
                  className="w-full px-3 py-2 bg-background border border-input rounded-md"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">
                  Client Secret {selectedIdP && '(leave blank to keep current)'}
                </label>
                <input
                  type="password"
                  value={formData.clientSecret}
                  onChange={(e) => setFormData({ ...formData, clientSecret: e.target.value })}
                  placeholder="********"
                  className="w-full px-3 py-2 bg-background border border-input rounded-md"
                  required={!selectedIdP}
                />
              </div>

              <div className="col-span-2">
                <label className="block text-sm font-medium mb-1">Scopes</label>
                <input
                  type="text"
                  value={formData.scopes}
                  onChange={(e) => setFormData({ ...formData, scopes: e.target.value })}
                  placeholder="openid email profile"
                  className="w-full px-3 py-2 bg-background border border-input rounded-md"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Space-separated list of OIDC scopes
                </p>
              </div>

              <div className="col-span-2 pt-4 border-t border-border">
                <h4 className="font-medium mb-3">Attribute Mapping</h4>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Email Attribute</label>
                <input
                  type="text"
                  value={formData.emailAttr}
                  onChange={(e) => setFormData({ ...formData, emailAttr: e.target.value })}
                  placeholder="email"
                  className="w-full px-3 py-2 bg-background border border-input rounded-md"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Name Attribute</label>
                <input
                  type="text"
                  value={formData.nameAttr}
                  onChange={(e) => setFormData({ ...formData, nameAttr: e.target.value })}
                  placeholder="name"
                  className="w-full px-3 py-2 bg-background border border-input rounded-md"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Picture Attribute</label>
                <input
                  type="text"
                  value={formData.pictureAttr}
                  onChange={(e) => setFormData({ ...formData, pictureAttr: e.target.value })}
                  placeholder="picture"
                  className="w-full px-3 py-2 bg-background border border-input rounded-md"
                />
              </div>
            </div>

            {/* Test Result */}
            {testResult && (
              <div className={`p-3 rounded-md ${testResult.success ? 'bg-green-500/10 border border-green-500/20' : 'bg-destructive/10 border border-destructive/20'}`}>
                <p className={`text-sm ${testResult.success ? 'text-green-600' : 'text-destructive'}`}>
                  {testResult.success ? testResult.message : testResult.error}
                </p>
                {testResult.discoveryInfo && (
                  <details className="mt-2">
                    <summary className="cursor-pointer text-sm text-muted-foreground">
                      Discovery Info
                    </summary>
                    <pre className="mt-2 text-xs overflow-auto">
                      {JSON.stringify(testResult.discoveryInfo, null, 2)}
                    </pre>
                  </details>
                )}
              </div>
            )}

            <div className="flex gap-2 pt-4">
              <button
                type="submit"
                disabled={isSaving}
                className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 disabled:opacity-50"
              >
                {isSaving ? 'Saving...' : selectedIdP ? 'Update' : 'Create'}
              </button>
              {selectedIdP && (
                <button
                  type="button"
                  onClick={handleTest}
                  disabled={isTesting}
                  className="px-4 py-2 bg-secondary text-secondary-foreground rounded-md hover:bg-secondary/80 disabled:opacity-50"
                >
                  {isTesting ? 'Testing...' : 'Test Connection'}
                </button>
              )}
              <button
                type="button"
                onClick={() => {
                  setShowForm(false);
                  resetForm();
                }}
                className="px-4 py-2 bg-secondary text-secondary-foreground rounded-md"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Redirect URI Info */}
      <div className="mt-6 p-4 bg-blue-500/10 border border-blue-500/20 rounded-lg">
        <h4 className="font-medium text-blue-600 mb-2">Configuration Info</h4>
        <p className="text-sm text-muted-foreground mb-2">
          When configuring your identity provider, use the following redirect URI:
        </p>
        <code className="block p-2 bg-background rounded text-sm break-all">
          {config?.cognito?.domain
            ? `https://${config.cognito.domain}.auth.${config.cognito.region}.amazoncognito.com/oauth2/idpresponse`
            : 'Loading...'}
        </code>
      </div>
    </div>
  );
}
